import { getAcombagoOrigins } from './portalConfig';
import { useSessionStore } from '../../stores/session/sessionStore';

// Dedupe a burst of failed connect-backed calls into a single re-nonce request. Reset after a short
// window so a later cold session can ask again (a successful re-nonce reloads the app and resets this).
let renonceInFlight = false;
let renonceTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Releases the dedupe guard so the next {@link requestPortalRenonce} is honoured immediately instead
 * of waiting out the window. Called when the host answers `nonce-refresh-error`: the attempt is over,
 * and a user-driven retry must not be swallowed as a duplicate of the one that just failed.
 */
export function clearRenonceGuard(): void {
    renonceInFlight = false;
    if (renonceTimeout !== null) {
        clearTimeout(renonceTimeout);
        renonceTimeout = null;
    }
}

/**
 * Asks the host (AcombaGo) to mint a fresh portal launch nonce. Used when the server-side
 * AcceoConnect session has gone cold (`connectSessionExpired`) — it can only be revived with a fresh
 * Identity Server token, which only the host has. The host replies with a `portal:nonce-refresh`
 * message (handled in usePortalEvents) carrying a fresh launch URL.
 *
 * Each message is posted to a specific host targetOrigin, so when the app is not actually embedded
 * the browser simply drops it (origin mismatch).
 *
 * Returns whether a recovery is now under way — true when this call posted one or one was already in
 * flight, false only when there is no trusted host origin to ask. Callers with nothing else to fall
 * back on need that distinction: a silent no-op would leave them waiting on a reply that can never
 * arrive, with no overlay and no error.
 */
export function requestPortalRenonce(): boolean {
    const origins = getAcombagoOrigins();
    if (origins.length === 0) return false;
    if (renonceInFlight) return true;

    renonceInFlight = true;
    // Drive the reconnecting overlay so recovery is visible, never a silent bounce.
    useSessionStore.getState().setReconnecting(true);
    renonceTimeout = setTimeout(() => {
        renonceInFlight = false;
        renonceTimeout = null;
        // Host never answered within the window — stop the overlay; a later cold call can ask again.
        useSessionStore.getState().setReconnecting(false);
    }, 30_000);

    // Post to each trusted host origin; only the parent whose origin matches receives the message.
    for (const origin of origins) {
        window.parent.postMessage({ type: 'portal:request-nonce' }, origin);
    }
    return true;
}

/**
 * Reloads the app into a fresh portal launch URL (with a new `?portal_nonce=`). Used when the host
 * replies to a re-nonce request; useAuthGuard then re-runs the portal exchange on load.
 */
export function reloadToPortalUrl(url: string): void {
    window.location.replace(url);
}
